/* ------------------------------------------------------------------
  File: LinkCard.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable clickable card component for the Accend Holdings website.
//   - Supports React Router navigation and external links.
//   - Supports icon, eyebrow, title, description, meta text, and visual action cue.
//   - Uses BaseCard as the foundational shell.
//   - Uses the acc-link-card namespace to avoid conflicts with Bootstrap.

/**
 * LinkCard
 *
 * A reusable clickable card component for navigating to internal or external pages.
 *
 * @param {React.ReactNode} icon
 * Optional icon displayed above the title.
 *
 * @param {string} eyebrow
 * Optional small label displayed above the title.
 *
 * @param {string} title
 * Main card title.
 *
 * @param {string} description
 * Supporting card description.
 *
 * @param {string} meta
 * Optional supporting metadata such as category, date, or status.
 *
 * @param {string} to
 * Internal route path for React Router navigation.
 *
 * @param {string} href
 * External URL or standard anchor link.
 *
 * @param {string} actionLabel
 * Text label for the card action cue.
 *
 * @param {"default" | "muted" | "soft" | "inverse"} [variant="default"]
 * Controls the BaseCard surface style.
 *
 * @param {"none" | "sm" | "md" | "lg"} [padding="md"]
 * Controls BaseCard internal spacing.
 *
 * @param {boolean} [bordered=true]
 * Controls whether the BaseCard has a border.
 *
 * @param {boolean} [fullHeight=false]
 * Makes the card stretch to the full height of its parent.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 *
 * @param {string} [target]
 * Anchor target for external links. Example: "_blank".
 *
 * @param {string} [rel]
 * Anchor rel attribute. Automatically set to "noopener noreferrer" when target is "_blank".
 */

import { Link } from "react-router-dom";
import { LuMoveRight } from "react-icons/lu";
import BaseCard from "./BaseCard";
import "./LinkCard.css";

const LinkCard = ({
  icon,
  eyebrow,
  title,
  description,
  meta,
  to,
  href,
  actionLabel = "Learn More",
  variant = "default",
  padding = "md",
  bordered = true,
  fullHeight = false,
  className = "",
  target,
  rel,
  ...props
}) => {
  const linkCardClasses = ["acc-link-card", className]
    .filter(Boolean)
    .join(" ");

  const content = (
    <BaseCard
      as="article"
      variant={variant}
      padding={padding}
      hoverable
      bordered={bordered}
      fullHeight={fullHeight}
      className={linkCardClasses}
    >
      <div className="acc-link-card__inner l-stack l-stack--sm">
        {(icon || eyebrow || meta) && (
          <div className="acc-link-card__top l-stack l-stack--xs">
            {icon && (
              <span className="acc-link-card__icon" aria-hidden="true">
                {icon}
              </span>
            )}

            {(eyebrow || meta) && (
              <div className="acc-link-card__meta-row l-cluster l-cluster--tight">
                {eyebrow && (
                  <span className="acc-link-card__eyebrow">{eyebrow}</span>
                )}

                {meta && <span className="acc-link-card__meta">{meta}</span>}
              </div>
            )}
          </div>
        )}

        <div className="acc-link-card__content l-stack l-stack--xs">
          {title && <h3 className="acc-link-card__title">{title}</h3>}

          {description && (
            <p className="acc-link-card__description">{description}</p>
          )}
        </div>

        <div className="acc-link-card__action" aria-hidden="true">
          <span className="acc-link-card__action-label">{actionLabel}</span>
          <span className="acc-link-card__action-icon">
            <LuMoveRight />
          </span>
        </div>
      </div>
    </BaseCard>
  );

  if (to) {
    return (
      <Link to={to} className="acc-link-card__link" {...props}>
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        className="acc-link-card__link"
        target={target}
        rel={target === "_blank" ? "noopener noreferrer" : rel}
        {...props}
      >
        {content}
      </a>
    );
  }

  return content;
};

export default LinkCard;